/**
 * French. Translated from the Dutch catalogue (`nl.ts`), which has the keys and the comments that explain them.
 *
 * - The user is addressed informally ("tu"), like the Dutch "je"; short instructions on buttons and prompts can be
 *   infinitives ("Saisir le code PIN").
 * - A no-break space (`\u00a0`) goes before `:`, `?` and `!`, so they never wrap onto a line of their own.
 * - "Manco" is translated as "anomalie", the usual French warehouse term for an order that cannot ship as is.
 */
import type {Catalogue} from './nl';

export const fr: Catalogue = {
    'common.close': 'Fermer',
    'common.cancel': 'Annuler',
    'common.error': 'Erreur',
    'common.retry': 'Réessayer',
    'common.loading': 'Chargement…',
    'common.unknownError': 'Erreur inconnue',
    'common.busy': 'Patiente… en attente du résultat.',
    'common.optional': '(facultatif)',

    'errorPage.heading': 'Cette page n’a pas pu être affichée',
    'errorPage.message':
        'Une erreur s’est produite dans le terminal et elle a été transmise aux développeurs. Réessaie, ou reviens à l’accueil si elle se reproduit.',
    'errorPage.loadFailed': 'Les données n’ont pas pu être chargées',
    'errorPage.back': 'Retour',
    'errorPage.home': 'Accueil',
    'notFound.heading': 'Cette page n’existe pas',
    'notFound.message': 'Vérifie l’adresse, ou reviens à l’accueil pour continuer.',

    'topNav.changeStation': 'Changer de poste',
    'topNav.logout': 'Déconnexion',
    'topNav.userMenu': 'Menu utilisateur de {name}',
    'topNav.language': 'Langue',
    'topNav.languageButton': 'Langue : {language}',
    'topNav.accountLanguage': 'Celle de mon compte ({language})',
    'topNav.menuArrowUp': 'Flèche vers le haut',
    'topNav.menuArrowDown': 'Flèche vers le bas',
    'topNav.menuPick': 'choisir',
    'topNav.menuConfirm': 'valider',
    'topNav.menuClose': 'fermer',
    'topNav.lightTheme': 'Thème clair',
    'topNav.darkTheme': 'Thème sombre',
    'topNav.environment': 'Environnement {environment}, passer à {other}',

    'update.available': 'Nouvelle version disponible',
    'update.reloadNow': 'Recharger',
    'update.reloadWhenIdle': 'rechargement dès que le terminal est libre',
    'update.reloadIn': 'rechargement automatique dans {time}',
    'update.seconds': {one: '{count} s', other: '{count} s'},
    'update.minutes': {one: '{count} min', other: '{count} min'},

    'environment.title': 'Attention',
    'environment.switch': 'Passer en development',
    'environment.cancel': 'Annuler',
    'environment.warning':
        'Le Shipping Terminal sera connecté à l’environnement de test et n’affichera aucune donnée de production. Cet environnement sert uniquement à tester de nouvelles fonctionnalités.',

    'login.title': 'Qui se connecte ?',
    'login.loadingUsers': 'Chargement des utilisateurs…',
    'pin.verifying': 'Vérification du code PIN…',
    'pin.prompt': 'Saisir ton code PIN',
    'pin.promptIdle': 'Inactif : saisir à nouveau ton code PIN',
    'pin.digit': 'Chiffre {index} sur {total}',
    'pin.changeUser': 'Changer d’utilisateur',
    'pin.errorInvalid': 'Code PIN incorrect ou trop de tentatives. Réessayer.',
    'pin.errorFailed': 'Erreur lors de la vérification du code PIN : {message}',

    'terminal.loadingPrinters': 'Chargement des imprimantes disponibles…',
    'terminal.noPrinters': 'Aucune imprimante trouvée',
    'terminal.selectStation': 'Choisir un poste',
    'terminal.selectStationHint': 'Clique sur l’imprimante de ce poste ou appuie sur son chiffre',
    'terminal.status.idle': 'Prêt à scanner',
    'terminal.status.notLive': 'Statut pas en direct',
    'terminal.status.mayBeOutdated': 'Statut peut-être pas à jour',

    'terminal.actions': 'Actions',
    'terminal.button.printLabel': 'Étiquette en plus',
    'terminal.button.manco': 'Anomalie',
    'terminal.button.deleteShipment': 'Supprimer les expéditions',
    'terminal.button.openInAdmin': 'Ouvrir dans l’admin',

    'order.number': 'Commande',
    'order.numberPlaceholder': 'Numéro de commande',
    'order.scan': 'Scanner',
    'order.pickedBy': 'Préparée par {user}',
    'order.pickedByUnknown': 'inconnu',
    'order.created': 'Créée le {date}',
    'order.scanPrompt': 'Scanner une commande',
    'order.scanHint': 'Ou tape le numéro et appuie sur Entrée',
    'scan.notRecognized': 'Scan non reconnu',
    'scan.notAnOrder':
        '« {code} » n’est ni un numéro de commande ni un code de conteneur. Scanne à nouveau ou saisis le numéro de commande (F2).',
    'scan.tooSlow':
        '« {code} » est arrivé trop lentement pour un scan. Scanne à nouveau ou saisis le numéro de commande dans le champ (F2).',
    'scan.invalidInput':
        'Saisis un numéro de commande d’au moins 6 chiffres, ou un code de conteneur commençant par B.',
    'scan.failed': 'Impossible de charger « {code} »',
    'scan.noOrderInContainer': 'Aucune commande ouverte dans ce conteneur',
    'order.products': 'À emballer',
    'order.shipping': 'Expédition',
    'order.noShipping': 'Aucun mode d’expédition',
    'order.warehouseNote': 'Note entrepôt',
    'order.noWarehouseNote': 'Aucune note entrepôt',
    'order.warehouseNoteRead': 'Lu',
    'order.warnings': 'Attention à l’emballage',
    'order.warning.hazard': 'Coller les étiquettes de danger',
    'order.warning.cans': 'Cette commande contient des bidons : attention à l’emballage',
    'order.warning.cargo': 'Expédition en fret : choisir la taille de palette à l’impression',
    'order.hazardImageAlt': 'Étiquette de danger',

    'meta.items': 'Articles',
    'meta.weight': 'Poids',
    'meta.largestSize': 'Taille max. (L × l × H)',
    'meta.centimeters': '{value} cm',
    'meta.notApplicable': 'N/A',

    'address.title': 'Adresse de livraison',

    'addressError.title': 'Corriger l’adresse',
    'addressError.hint': 'Corrige l’adresse et crée à nouveau l’étiquette.',
    'addressError.createLabel': 'Créer l’étiquette',
    'addressError.searchGoogle': 'Chercher l’adresse sur Google',
    'addressError.firstName': 'Prénom',
    'addressError.lastName': 'Nom',
    'addressError.company': 'Société',
    'addressError.street': 'Rue',
    'addressError.houseNumber': 'Numéro',
    'addressError.addition': 'Complément',
    'addressError.zipCode': 'Code postal',
    'addressError.city': 'Ville',
    'addressError.email': 'E-mail',
    'addressError.phone': 'Téléphone',
    'addressError.country': 'Pays',

    'country.NL': 'Pays-Bas',
    'country.BE': 'Belgique',
    'country.FR': 'France',
    'country.IT': 'Italie',
    'country.DE': 'Allemagne',

    'manco.title': 'Signaler une anomalie',
    'manco.address': 'Adresse incorrecte',
    'manco.customs': 'Erreur d’envoi international',
    'manco.other': 'Autre',
    'manco.reported': 'Anomalie signalée',
    'manco.failed': 'Impossible de signaler l’anomalie : {message}',

    'packing.title': 'Attention !',
    'packing.cans': 'Attention à l’emballage des bidons',

    'cargo.title': 'Fret',
    'cargo.pallet': 'Palette',
    'cargo.halfPallet': 'Demi-palette',
    'cargo.miniPallet': 'Mini-palette',
    'cargo.blockPallet': 'Palette bloc',
    'cargo.packages': 'Colis (stukgoed)',

    'printLabel.title': 'Étiquettes en plus',
    'printLabel.titleAuto': 'Imprimer les étiquettes',
    'printLabel.estimated': 'Étiquettes estimées : {count}',
    'printLabel.otherAmount': 'Autre quantité :',
    'printLabel.count': {one: '{count} étiquette', other: '{count} étiquettes'},
    'printLabel.print': 'Imprimer',

    'warningLabels.title': 'Attention !',
    'warningLabels.message': 'Cette commande a des étiquettes de danger !',
    'warningLabels.imageAlt': 'Étiquette de danger',

    'removeShipments.title': 'Supprimer les expéditions ?',
    'removeShipments.text':
        'Toutes les expéditions de la commande {id} seront supprimées et annulées auprès du transporteur. Le stock est réintégré et la commande repasse au statut payé.',

    'shipping.printWarning': 'Attention',
    'shipping.shipmentRemoved': 'Expéditions supprimées',
    'shipping.warningMancoReported':
        'Une anomalie a été signalée pour cette commande : elle n’est peut-être pas entièrement préparée',
    'shipping.orderFailed': 'La commande {id} n’a pas été expédiée',
    'shipping.stillPrinting': 'La commande {id} est encore en cours d’impression : scanne à nouveau une fois terminé',

    'removeShipments.failed': 'Les expéditions de la commande {id} n’ont pas été supprimées : {message}',
    'removeShipments.removeAnyway': 'Supprimer quand même',

    'outcome.unknownPrint': 'Impossible de savoir si les étiquettes de la commande {id} ont été imprimées',
    'outcome.unknownPrintHint':
        'Le serveur n’a pas donné de réponse claire. Vérifie l’imprimante et la commande dans l’admin (F11) avant de réimprimer.',
    'outcome.unknownRemove': 'Impossible de savoir si les expéditions de la commande {id} ont été supprimées',
    'outcome.unknownRemoveHint':
        'Le serveur n’a pas donné de réponse claire. Vérifie la commande dans l’admin (F11) avant de réessayer.',
    'outcome.unknownManco': 'Impossible de savoir si la commande {id} a été signalée',
    'outcome.unknownMancoHint':
        'Le serveur n’a pas donné de réponse claire. Vérifie la commande dans l’admin (F11) avant de la signaler à nouveau.',
    'outcome.busy': 'Une autre action d’expédition est encore en cours pour la commande {id}',
    'outcome.busyHint':
        'Attends qu’elle soit terminée, puis vérifie la commande dans l’admin (F11) avant de réessayer.',
    'outcome.retryAfter': {one: 'Réessaie dans {count} seconde.', other: 'Réessaie dans {count} secondes.'},
    'outcome.reason': 'Message du serveur : {message}',
    'outcome.printFailed': 'Les étiquettes de la commande {id} ont été créées, mais pas imprimées',
    'outcome.printFailedHint':
        'Ne réimprime pas, elles seraient créées en double. Supprime d’abord les expéditions (F10), puis réimprime.',
    'outcome.printFailedExtraHint':
        'Ne réimprime pas, elles seraient créées en double. Vérifie la commande dans l’admin (F11).',

    'apiError.notFound': 'Introuvable',
    'apiError.sessionExpired': 'La session a expiré. Reconnecte-toi.',
    'apiError.printerNotFound': 'L’imprimante est introuvable. Choisis à nouveau le poste d’emballage.',
    'apiError.personalAccountRequired': 'Cela nécessite un compte personnel, pas un compte partagé',
    'apiError.validationFailed': 'Données non valides : {message}',
    'apiError.internationalShipment': 'Envoi international : le service client l’expédie',
    'apiError.shipsWithParentOrder': 'C’est un reliquat : il part avec la commande principale',
    'apiError.tooEarlyForCarrier': 'Trop tôt pour le transporteur (PostNL à partir de 7h30)',
    'apiError.orderAlreadySent': 'La commande a déjà été expédiée',
    'apiError.orderNotPaid': 'La commande n’est pas payée',
    'apiError.vatNumberNotValidated': 'Le numéro de TVA du client n’a pas encore été vérifié',
    'apiError.shippingDateInFuture': 'La date d’expédition de la commande est dans le futur',
    'apiError.wrongPackingStation': 'Cette commande ne peut pas être expédiée depuis ce poste d’emballage',
    'apiError.paymentNotFound': 'Le paiement est introuvable chez le prestataire de paiement',
    'apiError.paymentAuthorizationLost': 'L’autorisation du paiement a expiré',
    'apiError.customerInvoiceMissing': 'La facture du client est manquante',
    'apiError.cargoShippingMethod': 'C’est un envoi en fret : choisis une taille de palette',
    'apiError.notCargoShippingMethod': 'Ce n’est pas un envoi en fret',
    'apiError.orderHasShipments': 'La commande a déjà des expéditions dont les étiquettes n’ont pas été imprimées',
    'apiError.orderHasNoShipments': 'La commande n’a pas d’expéditions',
    'apiError.recipientNameInvalid': 'Le transporteur refuse le nom',
    'apiError.postalCodeInvalid': 'Le transporteur refuse le code postal',
    'apiError.houseNumberInvalid': 'Le transporteur refuse le numéro ou son complément',
    'apiError.phoneInvalid': 'Le transporteur refuse le numéro de téléphone',
    'apiError.emailInvalid': 'Le transporteur refuse l’adresse e-mail',
    'apiError.addressInvalid': 'Le transporteur refuse la rue ou la ville',
    'apiError.servicePointInvalid': 'Le point relais n’est pas valide',
    'apiError.labelRejected': 'Le transporteur refuse l’étiquette : {message}',
    'apiError.adrConfigurationMissing': 'Les données ADR d’un produit sont manquantes',
    'apiError.shipmentFailed': 'L’expédition a échoué : {message}',
    'apiError.labelsNotPrinted': 'Les étiquettes ont été créées, mais pas imprimées',
    'apiError.orderBusy': 'Une autre action pour cette commande est encore en cours',
    'apiError.paymentProviderUnavailable': 'Le prestataire de paiement est injoignable. Réessaie dans un instant.',
};
